import React, { FunctionComponent, useState, useEffect } from "react"
import { Paper, CircularProgress, Grid, Typography } from "@material-ui/core"
import TransactionPageStyles from "./TransactionsPage.module.scss"
import { TransactionsTable, TransactionMetadata, TransactionModel } from "./TransactionsTable"
import { useLazyQuery } from "@apollo/client"
import {
    TransactionGroup,
    TransactionGroupPage,
    TransactionsSortFields
} from "../../../typings/types"
import LoadingSpinnerStyles from "../../common/LoadingSpinner.module.scss"
import { SearchInput } from "../../components/SearchInput"
import { SEARCH_TRANSACTIONS } from "../../queries/SEARCH_TRANSACTIONS"
import { useNotification } from "../../common/hooks/useNotification"
import { Paginate } from "../../components/Paginate"
import { Title } from "../../components/Title"

export const TransactionsPage: FunctionComponent = () => {
    const [transactions, setTransactions] = useState<TransactionModel[]>([])
    const [totalPages, setTotalPages] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [searchText, setSearchText] = useState("")
    const [activeSortFields, setActiveSort] = useState<TransactionsSortFields>({})
    const { notifyError } = useNotification()

    const TRANSACTION_GROUPS_PER_PAGE = 10

    const [searchTransactions, { data: searchData, loading: searchLoading }] = useLazyQuery<{
        searchTransactions: TransactionGroupPage
    }>(SEARCH_TRANSACTIONS, {
        variables: {
            searchParam: searchText,
            currentPage: currentPage,
            pageSize: TRANSACTION_GROUPS_PER_PAGE,
            sort: activeSortFields
        },
        onError: err => {
            notifyError("Error fetching transaction results.")
        },
        fetchPolicy: "network-only"
    })

    const updateTransactions = (groups: TransactionGroup[]) => {
        if (groups) {
            const transactions = [] as TransactionModel[]
            groups.forEach(group => {
                group.transactions.forEach(transaction => {
                    transactions.push({
                        ...transaction,
                        metadata: JSON.parse(transaction.metadata) as TransactionMetadata
                    })
                })
            })
            setTransactions(transactions)
        }
    }

    useEffect(() => {
        searchTransactions()
    }, [searchTransactions])

    useEffect(() => {
        if (searchData?.searchTransactions) {
            updateTransactions(searchData.searchTransactions.transactionGroups)
            setTotalPages(searchData.searchTransactions.totalPages)
        }
    }, [searchData])

    return (
        <>
            <Title text={"Transactions"} />
            <div className={TransactionPageStyles.TransactionsPage}>
                <div className={TransactionPageStyles.TransactionsPage__table}>
                    <Grid container direction="row" spacing={3} alignItems="center">
                        <Grid item>
                            <Typography
                                variant="h6"
                                id="tableTitle"
                                component="div"
                                data-testid="toolbarTitle"
                            >
                                Transactions
                            </Typography>
                        </Grid>
                        <Grid item style={{ flex: 1 }}>
                            <SearchInput callback={setSearchText} />
                        </Grid>
                    </Grid>
                    {searchLoading && (
                        <CircularProgress
                            className={LoadingSpinnerStyles.LoadingSpinner__absolute__center}
                            data-testid="transactionsLoading"
                        />
                    )}
                    <div className={TransactionPageStyles.TransactionsPage__content}>
                        <Paper>
                            <TransactionsTable
                                transactions={transactions}
                                setActiveSort={setActiveSort}
                            />
                            <div className={TransactionPageStyles.TransactionsPage__pagination}>
                                <Paginate
                                    totalPages={totalPages}
                                    currentPage={currentPage}
                                    callback={(newPage: number) => setCurrentPage(newPage)}
                                />
                            </div>
                        </Paper>
                    </div>
                </div>
            </div>
        </>
    )
}
